import React from 'react';
import styled from '@emotion/styled';
import { mixins } from '../../core/styles/theme/default';
import ImageWithCaption from '../../components/Image/ImageWithCaption';
import { Hidden, Grid, GridItem } from '../../helpers';
import { colors } from '../../core/styles/theme/default';
import Spacer from '../../helpers/Spacer';
import { rem } from 'polished';
import { ImageInterface } from '~/types';

const StyledQuote = styled.figure`
  ${mixins.listReset};
  display: flex;
  flex-direction: row;
`;

const StyledQuoteCell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;

  ${mixins.responsiveStyles({
    padding: { large: `0 ${rem(32)}` },
  })}
`;

const StyledBlockquote = styled.blockquote`
  padding: 0;
  font-weight: 400;
  line-height: 1.2;
  margin: 0;
  quotes: '«' '»' '‘' '’';
  color: ${colors.blue900};
  text-align: center;

  &::before {
    content: open-quote;
  }
  &::after {
    content: close-quote;
  }

  ${mixins.responsiveStyles({
    'font-size': {
      zero: `${rem(26)}`,
      small: `${rem(28)}`,
      medium: `${rem(33)}`,
      large: `${rem(38)}`,
      wide: `${rem(42)}`,
    },
  })}

  ${({ theme }) => theme?.stylesExtension?.quote?.quote}
`;

const StyledCaption = styled.figcaption`
  font-size: ${colors.p2};
  padding-top: ${rem(20)};
  color: ${colors.grey800};
  font-weight: 400;
  text-align: center;

  span {
    font-weight: 100;
  }

  ${({ theme }) => theme?.stylesExtension?.quote?.caption}
`;

const Quote = ({ data }: QuoteProps) => {
  const { quoteImage, quote, source } = data;
  const splittedSource = source.split(',');

  const hasImage = quoteImage?.image?.gatsbyImageData;

  if (hasImage) {
    quoteImage.imageSizes = {
      0: [370, 495],
      480: [365, 485],
      768: [310, 415],
      1024: [280, 375],
      1280: [255, 340],
      1440: [290, 390],
    };
  }

  return (
    <StyledQuote>
      <Grid columns={{ zero: 12 }}>
        {hasImage ? (
          <GridItem
            size={{ zero: 10, small: 6, medium: 4, large: 3 }}
            push={{ zero: 1, small: 3, medium: 4, large: 0 }}
          >
            <ImageWithCaption data={quoteImage} />
            <Hidden above="large">
              <Spacer space="regular" />
            </Hidden>
          </GridItem>
        ) : null}
        <GridItem
          size={{ zero: 'full', large: 8 }}
          push={{ zero: 'full', large: hasImage ? 0 : 2 }}
        >
          <StyledQuoteCell>
            <StyledBlockquote>{quote}</StyledBlockquote>
            <StyledCaption>
              {splittedSource[0] ? splittedSource[0] : splittedSource}
              {splittedSource[1] ? <>,</> : null}{' '}
              {splittedSource[1] ? <span>{splittedSource[1]}</span> : null}
            </StyledCaption>
          </StyledQuoteCell>
        </GridItem>
      </Grid>
    </StyledQuote>
  );
};

export type QuoteProps = {
  data: {
    quoteImage?: ImageInterface;
    quote: string;
    source: string;
  };
};

export default Quote;
